/**
 * Brand Resolution Utility
 * 
 * Resolves the current brand based on hostname and provides
 * brand-specific configurations for multi-brand support.
 */

export type BrandKey = 'elevation_electrical' | 'elevation_builders';

export interface BrandConfig {
  key: BrandKey;
  name: string;
  domain: string;
  buckets: {
    images: string;
    video: string;
  };
}

/**
 * Brand-specific storage bucket mapping
 */
export const BRAND_BUCKETS: Record<BrandKey, { images: string; video: string }> = {
  elevation_electrical: {
    images: 'electrical_images',
    video: 'electrical_video',
  },
  elevation_builders: {
    images: 'builders_images',
    video: 'builders_video',
  },
};

/**
 * Brand configuration mapping
 */
export const BRAND_CONFIGS: Record<BrandKey, BrandConfig> = {
  elevation_electrical: {
    key: 'elevation_electrical',
    name: 'Elevation Electrical',
    domain: 'elevationelectricalfl.com',
    buckets: BRAND_BUCKETS.elevation_electrical,
  },
  elevation_builders: {
    key: 'elevation_builders',
    name: 'Elevation Builders Group',
    domain: 'elevationbuildersgroup.com',
    buckets: BRAND_BUCKETS.elevation_builders,
  },
};

/**
 * Domain to brand mapping
 */
const DOMAIN_TO_BRAND: Record<string, BrandKey> = {
  'elevationelectricalfl.com': 'elevation_electrical',
  'www.elevationelectricalfl.com': 'elevation_electrical',
  'elevationbuildersgroup.com': 'elevation_builders',
  'www.elevationbuildersgroup.com': 'elevation_builders',
};

/**
 * Resolves the current brand based on the hostname
 * 
 * @returns The current brand key
 */
export const resolveBrand = (): BrandKey => {
  if (typeof window === 'undefined') {
    return 'elevation_electrical'; // Default for SSR
  }

  const host = window.location.host;
  
  // Check exact match first
  if (DOMAIN_TO_BRAND[host]) {
    return DOMAIN_TO_BRAND[host];
  }
  
  // Check if host contains any of the brand domains
  for (const [domain, brand] of Object.entries(DOMAIN_TO_BRAND)) {
    if (host.includes(domain)) {
      return brand;
    }
  }
  
  // Default to Elevation Electrical (current site)
  return 'elevation_electrical';
};

/**
 * Gets the current brand configuration
 * 
 * @returns The current brand config
 */
export const getCurrentBrandConfig = (): BrandConfig => {
  const brandKey = resolveBrand();
  return BRAND_CONFIGS[brandKey];
};

/**
 * Gets the storage bucket names for the current brand
 * 
 * @returns Object with images and video bucket names
 */
export const getCurrentBrandBuckets = () => {
  const brandKey = resolveBrand();
  return BRAND_BUCKETS[brandKey];
};

/**
 * Gets the storage bucket names for a specific brand
 * 
 * @param brandKey The brand key
 * @returns Object with images and video bucket names
 */
export const getBrandBuckets = (brandKey: BrandKey) => {
  return BRAND_BUCKETS[brandKey];
};
